/**
 * Classificação do destino resolvido e encaminhamento Premium vs Pro
 * (módulo puro — sem rede, sem IA, sem créditos).
 *
 * Problema real: "Bali" não é uma cidade. Depois de o utilizador escolher
 * "Bali, Indonésia", o pedido continuava a ser tratado como rota urbana
 * Premium. Uma ilha/província/país sem base concreta e com viagem de vários
 * dias pertence ao planeador Pro.
 */

export type DestinationCategory = "city" | "island" | "province" | "large_region" | "country";

const CITY_KINDS = new Set([
  "city",
  "town",
  "village",
  "hamlet",
  "municipality",
  "borough",
  "suburb",
  "locality",
  "postal_town",
  "quarter",
  "neighbourhood",
]);

const ISLAND_KINDS = new Set(["island", "islet", "archipelago"]);
const PROVINCE_KINDS = new Set(["province", "state", "state_district"]);
const REGION_KINDS = new Set([
  "region",
  "county",
  "district",
  "territory",
  "peninsula",
  "national_park",
  "protected_area",
  "administrative",
]);

/** Categoria do lugar a partir do tipo devolvido pelo geocodificador. */
export function classifyPlaceKind(kind: string | null | undefined): DestinationCategory {
  const value = (kind ?? "").toLowerCase().trim();
  if (!value) return "city";
  if (CITY_KINDS.has(value)) return "city";
  if (value === "country") return "country";
  if (ISLAND_KINDS.has(value)) return "island";
  if (PROVINCE_KINDS.has(value)) return "province";
  if (REGION_KINDS.has(value)) return "large_region";
  return "city";
}

export const isAreaCategory = (category: DestinationCategory): boolean => category !== "city";

export type DestinationRouting =
  | { target: "premium" }
  | { target: "pro"; reason: "country" | "area_multi_day" };

/**
 * País sem cidade → Pro. Ilha/província/região grande sem base concreta e com
 * mais de um dia → Pro. Tudo o resto continua no Premium como rota local.
 */
export function decideDestinationRouting(input: {
  category: DestinationCategory;
  days?: number | null;
  /** True quando o pedido já nomeia uma base concreta ("Ubud, Bali"). */
  hasExplicitBase?: boolean;
}): DestinationRouting {
  const { category } = input;
  if (input.hasExplicitBase && category !== "country") return { target: "premium" };
  if (category === "country") return { target: "pro", reason: "country" };
  if (category === "city") return { target: "premium" };
  const days = Math.max(1, Math.round(Number(input.days) || 1));
  if (days > 1) return { target: "pro", reason: "area_multi_day" };
  return { target: "premium" };
}
